import jsonata from 'jsonata'
import mem from 'mem'
import 'regenerator-runtime'
import { NIL, v4, v5 } from 'uuid'
import { consolidateExpressions } from './consolidateExpressions'
import { findExpressions } from './findExpressions'
import { substring } from '@the-platform-company/ts-utilities'
import {
  ArrayPath,
  getStringPath,
  setByPath,
} from '@the-platform-company/path-utilities'
import isPromise from 'p-is-promise'
import {
  generateRandomString,
  sha256,
  hexToBase64URL,
  hexToBase64,
} from './stringFunctions'
import { Range } from './range'

const defaultMaxAge = 5 * 60 * 1000

export type ResolveJsonataOptions = {
  maxAge?: number
  onError?: (error: unknown) => void
  consolidateExpressions?: boolean
  maxDepth?: number
  evaluatePlainExpression?: boolean
  resolveResult?: boolean
  bindings?: Record<string, any>
  functions?: {
    name: string
    implementation: (this: jsonata.Focus, ...args: any[]) => any
    signature?: string
  }[]
  replacer?: (value: any) => any
}

const handleError =
  (options: ResolveJsonataOptions = {}) =>
  (reason: unknown) => {
    if (options.onError) {
      options.onError(reason)
    } else {
      throw reason
    }
  }

const compileExpression = (input: string, options: ResolveJsonataOptions) => {
  const compiledExpression = jsonata(
    options.consolidateExpressions ? consolidateExpressions(input) : input
  )

  compiledExpression.registerFunction(
    'uuid',
    (name, namespace) => (name ? v5(name, namespace ?? NIL) : v4()),
    '<s?s?:s>'
  )

  compiledExpression.registerFunction(
    'resolveJsonata',
    (input, data, optionsArg) =>
      resolveJsonata(input, data, optionsArg ?? options),
    '<jj?o?:j>'
  )

  compiledExpression.registerFunction(
    'setByPath',
    (obj, path, value, generative) => setByPath(obj, path, value, generative),
    '<jsjb?:j>'
  )

  compiledExpression.registerFunction(
    'generateRandomString',
    generateRandomString,
    '<n:s>'
  )

  compiledExpression.registerFunction('sha256', sha256, '<s:s>')

  compiledExpression.registerFunction('hexToBase64URL', hexToBase64URL, '<s:s>')

  compiledExpression.registerFunction('hexToBase64', hexToBase64, '<s:s>')

  for (const func of options.functions ?? []) {
    compiledExpression.registerFunction(
      func.name,
      func.implementation,
      func.signature
    )
  }

  if (options.replacer) {
    const originalEvaluate = compiledExpression.evaluate
    const wrappedEvaluate = ((...params: Parameters<typeof originalEvaluate>) =>
      options.replacer
        ? options.replacer(originalEvaluate(...params))
        : originalEvaluate(...params)) as typeof originalEvaluate
    compiledExpression.evaluate = wrappedEvaluate
  }

  return compiledExpression
}

const cacheFactory = <T extends any>(func: (...params: any[]) => T) =>
  mem((maxAge: number) =>
    maxAge > 0 ? mem(func, { maxAge, cacheKey: JSON.stringify }) : func
  )

const cachedFindExpressionsFactory = cacheFactory(findExpressions)
const cachedCompileExpressionFactory = cacheFactory(compileExpression)

const replaceSubexpressions = (
  input: string,
  results: { range: Range; result: any }[]
) => {
  let currentOffset = 0

  for (const { range, result } of results) {
    const lengthBeforeReplacing = input.length

    const before = substring(input, 0, range[0] - 2 - currentOffset)
    const after = substring(input, range[1] + 2 - currentOffset)

    if (typeof result === 'object' && result !== null) {
      input = before + JSON.stringify(result) + after
    } else if (result !== undefined) {
      input = before + result + after
    } else {
      input = before + after
    }

    currentOffset += lengthBeforeReplacing - input.length
  }

  return input
}

const resolveJsonataStringInternal = (
  input: string,
  data: any,
  options: ResolveJsonataOptions,
  depth: number,
  path: (string | number)[]
) => {
  const cachedFindExpressions = cachedFindExpressionsFactory(
    options.maxAge ?? defaultMaxAge
  )
  const cachedCompileExpression = cachedCompileExpressionFactory(
    options.maxAge ?? defaultMaxAge
  )

  try {
    if (options.evaluatePlainExpression) {
      const result = cachedCompileExpression(input, options).evaluate(
        data,
        options.bindings
      )

      return result
    }

    const ranges = cachedFindExpressions(input)

    if (options.consolidateExpressions) {
      return cachedCompileExpression(input, options).evaluate(
        data,
        options.bindings
      )
    }

    if (
      ranges.length === 1 &&
      ranges[0][0] === 2 &&
      ranges[0][1] === input.length - 2
    ) {
      if (input.length > 4) {
        const expression = substring(input, ...ranges[0])

        const result = cachedCompileExpression(expression, options).evaluate(
          data,
          options.bindings
        )

        if (options.resolveResult) {
          return resolveJsonataInternal(result, data, options, depth + 1, path)
        }

        return result
      } else {
        return undefined
      }
    }

    const results = ranges.map((range) => {
      const expression = substring(input, ...range)

      const result = expression.trim()
        ? cachedCompileExpression(expression, options).evaluate(
            data,
            options.bindings
          )
        : undefined

      if (result !== undefined && options.resolveResult) {
        return {
          range,
          result: resolveJsonataInternal(
            result,
            data,
            options,
            depth + 1,
            path
          ),
        }
      }

      return {
        range,
        result,
      }
    })

    return results.some((r) => typeof r?.result?.then === 'function')
      ? Promise.all(
          results.map(async (r) => ({
            range: r.range,
            result: await resolveDeep(r.result),
          }))
        ).then((res) => replaceSubexpressions(input, res))
      : replaceSubexpressions(input, results)
  } catch (error) {
    error =
      typeof error === 'object' && error !== null
        ? Object.assign(error, { expression: input, path: getStringPath(path) })
        : error

    throw error
  }
}

const resolveJsonataInternal = (
  input: any,
  data: any,
  options: ResolveJsonataOptions,
  depth: number,
  path: (string | number)[]
) => {
  switch (typeof input) {
    case 'object': {
      if (options.maxDepth != null && depth >= options.maxDepth) {
        return input
      }

      if (!input) {
        return input
      }

      if (Array.isArray(input)) {
        return input.map((item, index) =>
          resolveJsonataInternal(item, data, options, depth + 1, [
            ...path,
            index,
          ])
        )
      }

      return Object.fromEntries(
        Object.entries(input).map(([key, value]) => [
          key,
          resolveJsonataInternal(value, data, options, depth + 1, [
            ...path,
            key,
          ]),
        ])
      )
    }
    case 'string': {
      return resolveJsonataStringInternal(input, data, options, depth, path)
    }
    default:
      return input
  }
}

export const resolveJsonata = (
  input: any,
  data: any,
  options: ResolveJsonataOptions = {}
) => {
  try {
    return resolveJsonataInternal(input, data, options, 0, [])
  } catch (error) {
    handleError(options)(error)
  }
}

const resolveDeep = <T>(obj: T | Promise<T>) => {
  const resolve = (res: T) => {
    if (Array.isArray(res)) {
      const entries = res.map(resolveDeep)
      if (entries.some((v) => isPromise(v))) {
        return Promise.all(entries.map(async (v) => await v))
      }

      return entries
    }

    if (res != null && Object.getPrototypeOf(res) === Object.prototype) {
      const entries = Object.entries(res).map(([k, v]) => [k, resolveDeep(v)])

      if (entries.some(([, v]) => isPromise(v))) {
        return Promise.all(entries.map(async ([k, v]) => [k, await v])).then(
          (entries) => Object.fromEntries(entries)
        )
      }

      return Object.fromEntries(entries)
    }

    return res
  }

  return isPromise(obj) ? obj.then((res) => resolve(res)) : resolve(obj)
}

export const resolveJsonataPromises = (
  input: any,
  data: any,
  options: ResolveJsonataOptions = {}
) => {
  let result
  try {
    result = resolveDeep(resolveJsonataInternal(input, data, options, 0, []))

    if (isPromise(result)) {
      return result.catch(handleError(options))
    }

    return result
  } catch (error) {
    handleError(options)(error)
  }
}
